import React, { useState, useEffect, forwardRef, Fragment, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import {
    IconButton,
    useTheme,
    Grid,
    Paper,
    Button,
    Menu,
    MenuItem
} from "@mui/material";
import { AddBox } from '@mui/icons-material';

import DownArrowIcon from '../../../icons/DownArrowIcon';
import UpArrowIcon from '../../../icons/UpArrowIcon';
import OptionsIcon from '../../../icons/OptionsIcon';

import ViewToggle from '../../ViewToggle';

import { useDebounce } from '../../../../utils/Utils';

import store from '../../../../store';
import { setAutomaticProjectListFilters, setFloorPlanListFilters, setProjectListFilters, toggleProjectListFilterOptionsExpanded } from '../../../../store/actions/FilterView';
import { setProjectSort } from '../../../../store/actions/FilterView';
import StyledTextField from '../../../styled/TextField';
import { useLocation, useNavigate } from 'react-router-dom';

const Title = ({ nrOfProjects, t, title, type }) => {
    const navigate = useNavigate();

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'flex-start',
                height: '100%',
                gap: '10px'
            }}
        >
            <h1 style={{ margin: 0, fontSize: window.innerWidth> 650 ? '2em' : '1em' }}>{title}</h1>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '24px',
                    height: '18px',
                    marginTop: '5px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    backgroundColor: '#F16868',
                    padding: window.innerWidth > 650 ? 0 : '5px',
                    color: '#FFF'
                }}
            >
                {nrOfProjects}
            </div>
            {
                type == 'floorPlan' && (
                    <Button onClick={() => navigate('/app/easy-floor/create')} title={t('Add new floor plan')} startIcon={<AddBox />} variant='contained'>
                        {t('Add new')}
                    </Button>
                )
            }
        </div>
    )
}

const ActionButtons = ({ isExpanded }) => {
    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}>
            <div style={{ paddingLeft: '30px' }}>
                <ViewToggle />
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', paddingLeft: '30px'}}>
                <ToggleFilterOptions isExpanded={isExpanded} />
            </div>
        </div>
    )
}

const ToggleFilterOptions = ({ isExpanded }) => {
    const theme = useTheme();

    return (
        <IconButton
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '40px',
                height: '40px',
                border: '1px solid ' + theme.palette.border.primary,
                borderRadius: '8px'
            }}
            onClick={() => {
                const value = !isExpanded

                store.dispatch(toggleProjectListFilterOptionsExpanded(value))
                localStorage.setItem('isExpanded', value)
            }}
        >
            {isExpanded ? <UpArrowIcon /> : <DownArrowIcon />}
        </IconButton>
    )
}

const Expanded = (props) => {
    const { title, type } = props;
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = useState(null)
    const open = Boolean(anchorEl)

    const projectSort = useSelector((state) => state.filterView.projectSort)

    const handleOpenSortMenu = (event) => { setAnchorEl(event.currentTarget) }
    const handleCloseSortMenu = () => { setAnchorEl(null) }

    const sortBy = (attribute, value) => {
        const sortArray = JSON.parse(projectSort);
        sortArray['name'] = null;
        sortArray['date'] = null;

        sortArray[attribute] = value;

        store.dispatch(setProjectSort(JSON.stringify(sortArray)))
    }

    const DatePickerInput = forwardRef(({ startDate, endDate,  onClick, t }, ref) => (
        <StyledTextField
            id="date"
            size="small"
            variant="outlined"
            fullWidth
            sx={{ maxWidth: '720px' }}
            ref={ref}
            onClick={onClick}
            defaultValue={
                startDate && endDate
                ? new Date(startDate).toLocaleDateString() + '-' + new Date(endDate).toLocaleDateString()
                : t('All time')
            }
        />
    ));
    
    return (
        <div style={{ padding: '24px', paddingBottom: 0 }}>
            <Paper style={{padding: '24px'}}>
                <Grid container spacing={2} style={{ backgroundColor: theme.palette.background.secondary }}>
                    <Grid item xl={6} lg={6} md={6} sm={6} xs={6} style={{paddingTop: 0}}>
                        <Title nrOfProjects={props.nrOfProjects} t={props.t} title={title} type={type} />
                    </Grid>
                    <Grid item xl={6} lg={6} md={6} sm={6} xs={6} style={{ display: 'flex', justifyContent: 'end' }}>
                        {
                            window.innerWidth > 650
                                ? (
                                    <Button
                                        variant='outlined'
                                        onClick={handleOpenSortMenu}
                                        startIcon={<OptionsIcon />}
                                        style={{
                                            color: theme.palette.text.primary,
                                            borderColor: theme.palette.border.main
                                        }}
                                    >
                                        {props.t('Sort by')}
                                    </Button>
                                )
                                : (
                                    <>
                                        <IconButton onClick={handleOpenSortMenu}>
                                            <OptionsIcon />
                                        </IconButton>
                                    </>
                                )
                        }
                        <Menu
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleCloseSortMenu}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'left'
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'left'
                            }}
                            sx={{
                                '& .MuiPaper-root': {
                                    backgroundColor: theme.palette.background.navbarButton
                                }
                            }}
                        >
                            <MenuItem onClick={() => sortBy('name', 'ASC')} style={{ gap: '10px' }}>
                                {props.t('Name')+' '+props.t('ASC')}
                            </MenuItem>
                            <MenuItem onClick={() => sortBy('name', 'DESC')} style={{ gap: '10px' }}>
                                {props.t('Name')+' '+props.t('DESC')}
                            </MenuItem>
                            <MenuItem onClick={() => sortBy('date', 'ASC')} style={{ gap: '10px' }}>
                                {props.t('Date')+' '+props.t('ASC')}
                            </MenuItem>
                            <MenuItem onClick={() => sortBy('date', 'DESC')} style={{ gap: '10px' }}>
                                {props.t('Date')+' '+props.t('DESC')}
                            </MenuItem>
                        </Menu>
                    </Grid>

                    <Grid item xl={3} lg={3} md={12} sm={12} xs={12}>
                        <label htmlFor="name">{props.t('Search project')}</label>
                        <StyledTextField
                            id="name"
                            size="small"
                            variant="outlined"
                            fullWidth
                            sx={{ maxWidth: '720px' }}
                            value={props.filterName}
                            defaultValue={props.filterName}
                            onChange={props.onFilterNameChanged}
                        />
                    </Grid>
                    <Grid item xl={3} lg={3} md={12} sm={12} xs={12}>
                        <label htmlFor="date">{props.t('Date')}</label>
                        <DatePicker
                            selectsRange={true}
                            isClearable={true}
                            startDate={props.startDate}
                            endDate={props.endDate}
                            onChange={(update) => {
                                props.onFilterDateChanged(update)
                            }}
                            customInput={
                                <DatePickerInput
                                    startDate={props.startDate}
                                    endDate={props.endDate}
                                    t={props.t}
                                />
                            }
                            style={{ width: '100%' }}
                        />
                    </Grid>
                    <Grid item xl={6 } lg={3} md={12} sm={12} xs={12} style={{ display: 'flex', justifyContent: 'end' }}>
                        <ActionButtons isExpanded={props.isExpanded} />
                    </Grid>
                </Grid>
            </Paper>
        </div>
    )
}

const Decreased = (props) => {
    const theme = useTheme();
    const { title, type } = props;

    const [hidden, setHidden] = useState(true);

    const inputRef = useRef();

    return (
        <Grid
            container
            spacing={2}
            style={{
                backgroundColor: theme.palette.background.secondary,
                padding: '24px'
            }}
        >
            <Grid item xl={3} lg={3} md={12} sm={12} xs={12}>
                <Title nrOfProjects={props.nrOfProjects} t={props.t} title={title} type={type} />
            </Grid>
            <Grid item xl={6} lg={6} md={12} sm={12} xs={12}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <StyledTextField
                        size="small"
                        label="Search project"
                        variant="outlined"
                        fullWidth
                        sx={{ maxWidth: '720px' }}
                        value={props.filterName}
                        onChange={props.onFilterNameChanged}
                    />
                </div>
            </Grid>
            <Grid item xl={3} lg={3} md={12} sm={12} xs={12}>
                <ActionButtons isExpanded={props.isExpanded} />
            </Grid>
        </Grid>
    )
}

export default function ProjectListHeader({ nrOfProjects, title, type }) {
    const theme = useTheme();
    const {t} = useTranslation();
    const location = useLocation();

    const isExpanded = useSelector((state) => state.filterView.projectListFilterOptionsExpanded);

    const floorPlanListFilters = useSelector((state) => state.filterView.floorPlanListFilters);
    const projectListFilters = useSelector((state) => state.filterView.projectListFilters);
    const automaticProjectListFilters = useSelector((state) => state.filterView.automaticProjectListFilters);

    const [filterName, setFilterName] = useState('')
    const [filterDateRange, setFilterDateRange] = useState([null, null])
    const [startDate, endDate] = filterDateRange;

    const debouncedFilterName = useDebounce(filterName, 500);

    useEffect(() => {
        const filters = {'name': debouncedFilterName, 'date': filterDateRange};

        if (type == 'planograms') {
            store.dispatch(setProjectListFilters(filters));
        } else if (type == 'automatic-planograms') {
            store.dispatch(setAutomaticProjectListFilters(filters));
        } else if (type == 'floorPlan') {
            store.dispatch(setFloorPlanListFilters(filters));
        }
    }, [debouncedFilterName, filterDateRange]);

    const onFilterNameChanged = (event) => { setFilterName(event.target.value) }

    const onFilterDateChanged = (update) => {
        setFilterDateRange(update)

        if((!update[0] && !update[1]) || (update[0] && update[1])) {
            const filters = {'name': filterName, 'date': update}

            store.dispatch(setProjectListFilters(filters));
        }
    }

    const renderHeader = () => {
        if(isExpanded) {
            return <Expanded
                isExpanded={isExpanded}
                filterName={filterName}
                startDate={startDate}
                endDate={endDate}
                nrOfProjects={nrOfProjects}
                onFilterNameChanged={onFilterNameChanged}
                onFilterDateChanged={onFilterDateChanged}
                t={t}
                title={title}
                type={type}
            />
        } else {
            return <Decreased
                isExpanded={isExpanded} 
                filterName={filterName}
                nrOfProjects={nrOfProjects}
                onFilterNameChanged={onFilterNameChanged}
                t={t}
                title={title}
                type={type}
            />
        }
    }

    return (
        <div id='project-list-header'>
            {renderHeader()}
        </div>
    );
}
